import { toast } from "react-toastify";
import { useState, useEffect } from "react";

export const useGetApi = ({
  apiFunction,
  params = {},
  dependencies = [],
  debounceDelay = 0,
  dataKey,
  countKey,
  skip = false,
}) => {
  const [dataList, setDataList] = useState(dataKey ? [] : null);
  const [dataCount, setDataCount] = useState(0);
  const [isLoading, setIsLoading] = useState(
    !apiFunction || skip ? false : true
  );
  const [isError, setIsError] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    setIsError(false);
    
    try {
      const response = await apiFunction(params);
      
      if (response?.success || response?.code === 200) {
        let dataArray = [];
        let count = 0;
        
        if (Array.isArray(response.data)) {
          dataArray = response.data;
          count = response.count || dataArray.length;
        } else if (Array.isArray(response)) {
          dataArray = response; // In case the API returns the array directly
          count = dataArray.length;
        } else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
          // If data is an object, try to extract using dataKey and countKey
          const dataObj = response.data;
          
          // Use dataKey if provided, otherwise try to find array
          if (dataKey && dataObj[dataKey]) {
            dataArray = dataObj[dataKey];
          } else {
            const possibleArrayKeys = Object.keys(dataObj).filter(key => Array.isArray(dataObj[key]));
            if (possibleArrayKeys.length > 0) {
              dataArray = dataObj[possibleArrayKeys[0]];
            }
          }
          
          // Use countKey if provided, otherwise try common count fields
          if (countKey && dataObj[countKey] !== undefined) {
            count = dataObj[countKey];
          } else if (dataObj.count !== undefined) {
            count = dataObj.count;
          } else if (dataObj.total !== undefined) {
            count = dataObj.total;
          } else {
            count = dataArray.length;
          }
        }
        
        setDataList(dataArray);
        setDataCount(count);
      } else {
        setIsError(true);
        toast.error(response?.message || "Failed to fetch data.");
        setDataList([]);
        setDataCount(0);
      }
    } catch (error) {
      setIsError(true);
      toast.error(error?.message || "Failed to fetch data.");
      setDataList([]);
      setDataCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let timer = null;
    if (!apiFunction || skip) {
      return;
    }

    const executeFetch = () => {
      fetchData();
    };

    if (params?.search !== undefined) {
      timer = setTimeout(executeFetch, debounceDelay);
    } else {
      executeFetch();
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [...dependencies, JSON.stringify(params)]);

  return { dataList, dataCount, isLoading, isError, refetch: fetchData };
};
